import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as Modal from '_components/modal/generic-modal'
import { FormProvider, useForm } from 'react-hook-form'
import { Attachments } from '_components/refera-ui'
import { Typography } from '@mui/material'
import useStyles from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { serviceOrderCurrentSelector } from '_/modules/service-orders/selectors'
import { useParams } from '@reach/router'
import classNames from 'classnames'
import { sendInvoice } from '_/modules/service-orders/actions'
import { getIsXmlRequiredToFinishServiceOrder } from '_/modules/finance/actions'
import { isXmlRequiredToFinishServiceOrderSelector } from '_/modules/finance/selectors'
import { useToast } from '_/hooks/use-toast'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { useDialog } from '_/hooks/use-dialog'
import { getBudgetByIdSelector } from '_/modules/budget/selectors'
// import { BasicInput } from '_/components/inputs'

const DEFAULT_ATTACHMENTS = { pdf: [], image: [], xml: [], txt: [] }
const INVOICE_PDF_MAX_SIZE = 1000000
const INVOICE_XML_MAX_SIZE = 500 * 1024
const INVOICE_IMAGE_MAX_SIZE = 5 * 1024 * 1024

const isSaoPauloCity = cityName => {
  if (!cityName) return false
  const normalizedCity = cityName.toLowerCase().trim()
  return normalizedCity === 'são paulo' || normalizedCity === 'sao paulo'
}

export function InvoiceModal({ open, onClose, isEditing }) {
  const styles = useStyles()
  const { serviceOrderId, budgetId } = useParams()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const serviceOrder = useSelector(serviceOrderCurrentSelector)
  const isXmlRequiredToFinishServiceOrder = useSelector(isXmlRequiredToFinishServiceOrderSelector)
  const budget = useSelector(getBudgetByIdSelector(budgetId))

  const [attachments, setAttachments] = useState(DEFAULT_ATTACHMENTS)
  const [isTxtFile, setIsTxtFile] = useState(false)
  const [loading, setLoading] = useState(true)
  const isSaasAgency = useMemo(
    () => serviceOrder?.agency?.get('contractType') === 'SAAS',
    [serviceOrder]
  )

  const methods = useForm({
    mode: 'all',
  })

  // const invoiceNumber = methods.watch('invoiceNumber')

  const handleDownloadInvoice = extension => {
    const item = attachments?.[extension]?.[0]
    const url = item?.file || (item instanceof File ? URL.createObjectURL(item) : '')
    if (url) window.open(url)
  }

  const handleRemoveInvoice = extension => {
    setAttachments(prev => ({
      ...prev,
      [extension]: [],
    }))
    if (extension === 'xml' || extension === 'txt') {
      setIsTxtFile(false)
    }
  }

  const validateSaoPauloTxtStructure = fileContent => {
    try {
      const lines = fileContent.split('\n')
      if (lines.length < 2) {
        return false
      }

      const firstLine = lines[0]
      if (!firstLine.startsWith('1') || firstLine.length !== 29) {
        return false
      }

      return true
    } catch {
      return false
    }
  }

  const convertAddinToXml = addinFile => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        const fileContent = reader.result

        try {
          const parser = new DOMParser()
          const xmlDoc = parser.parseFromString(fileContent, 'application/xml')

          const parseError = xmlDoc.getElementsByTagName('parsererror')
          if (parseError.length) {
            reject(new Error('Arquivo não contém XML válido.'))
            return
          }

          const xmlBlob = new Blob([fileContent], { type: 'application/xml' })
          resolve(xmlBlob)
        } catch (err) {
          reject(new Error('Erro ao processar o arquivo XML.'))
        }
      }

      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo ADDIN.'))
      }

      reader.readAsText(addinFile)
    })
  }

  const handleAddInvoice = (extension, file) => {
    if (
      (extension === 'pdf' && file[0].size > INVOICE_PDF_MAX_SIZE) ||
      (extension === 'image' && file[0].size > INVOICE_IMAGE_MAX_SIZE) ||
      (extension !== 'pdf' && extension !== 'image' && file[0].size > INVOICE_XML_MAX_SIZE)
    ) {
      showToast({
        type: 'error',
        message: `O arquivo ultrapassou o limite de tamanho permitido: ${
          extension === 'pdf' ? '1MB' : extension === 'image' ? '5MB' : '500KB'
        }`,
      })
      return
    }

    if (extension === 'image') {
      setAttachments(prev => ({ ...prev, image: file, pdf: [] }))
      return
    }

    if (extension !== 'pdf') {
      if (extension === 'txt' || extension === 'asm') {
        const companyCity = budget.toJS().tradesman.city

        if (isSaoPauloCity(companyCity)) {
          const reader = new FileReader()
          reader.onload = () => {
            const fileContent = reader.result

            if (validateSaoPauloTxtStructure(fileContent)) {
              const txtFile = new File([file[0]], 'nota_fiscal.txt', {
                type: 'text/plain',
              })
              setAttachments(prev => ({ ...prev, xml: [txtFile] }))
              setIsTxtFile(true)
            } else {
              showToast({
                type: 'error',
                message: 'Erro ao converter arquivo em TXT.',
              })
            }
          }
          reader.readAsText(file[0])
        } else {
          showToast({
            type: 'error',
            message: 'Erro ao converter arquivo em TXT',
          })
        }
      } else {
        convertAddinToXml(file[0])
          .then(xmlBlob => {
            const xmlFile = new File([xmlBlob], 'nota_fiscal.xml', {
              type: 'application/xml',
            })
            setAttachments(prev => ({ ...prev, xml: [xmlFile] }))
            setIsTxtFile(false)
          })
          .catch(() => {
            showToast({
              type: 'error',
              message: 'Erro ao converter arquivo em XML.',
            })
          })
      }
    } else {
      setAttachments(prev => ({ ...prev, [extension]: file, image: [] }))
    }
  }

  const isXmlNotRequired =
    !serviceOrder?.isReferaService && serviceOrder?.agency?.get('contractType') === 'SAAS'

  const isPdfNotRequired =
    serviceOrder?.agency?.get('contractType') === 'SAAS' &&
    !serviceOrder?.isReferaService &&
    !serviceOrder?.agency?.get('nfRequired')

  const isSubmitButtonDisabled = useMemo(() => {
    const hasPdf = attachments?.pdf?.length > 0
    const hasImage = attachments?.image?.length > 0
    const hasXml = attachments?.xml?.length > 0

    const invoiceUploaded = hasPdf || hasImage

    const xmlRequired = isXmlRequiredToFinishServiceOrder?.isXmlRequired && !isXmlNotRequired
    const pdfRequired = !isPdfNotRequired

    if (loading) return true
    if (!invoiceUploaded && !hasXml) return true

    if (pdfRequired) {
      if (isSaasAgency) {
        if (!invoiceUploaded) return true
      } else if (!hasPdf) return true
    }

    if (xmlRequired && !hasXml) return true

    const invoiceAlreadyUploaded = hasPdf ? attachments?.pdf[0]?.id : attachments?.image?.[0]?.id
    const xmlAlreadyUploaded = attachments?.xml[0]?.id

    if (invoiceAlreadyUploaded && xmlAlreadyUploaded) return true

    return false
  }, [
    attachments,
    loading,
    isXmlRequiredToFinishServiceOrder,
    isXmlNotRequired,
    isPdfNotRequired,
    isSaasAgency,
  ])

  const handleSendInvoice = useCallback(async () => {
    const formData = new FormData()

    const pdfFile = attachments?.pdf[0]
    const imageFile = attachments?.image?.[0]
    const xmlFile = attachments?.xml[0]

    // checking if the file is already uploaded
    if (imageFile && !imageFile?.id) {
      formData.append('attachment_image', imageFile)
    } else if (pdfFile && !pdfFile?.id) {
      formData.append('attachment_pdf', pdfFile)
    }
    if (xmlFile && !xmlFile?.id) {
      if (isTxtFile) {
        formData.append('attachment_txt', xmlFile)
      } else {
        formData.append('attachment_xml', xmlFile)
      }
    }

    await dispatch(sendInvoice(serviceOrderId, formData))
      .then(onClose)
      .catch(err => {
        showDialog({
          type: 'error',
          subject: 'Erro ao enviar notas fiscais',
          description: (
            <span style={{ whiteSpace: 'pre-wrap' }}>{formatErrorMessage(err, 1000)}</span>
          ),
          onApprove: closeDialog,
          labelApprove: 'Ok',
        })
      })
  }, [attachments, onClose])

  useEffect(() => {
    if (serviceOrder?.get('attachments').size > 0) {
      const invoiceList = serviceOrder
        .get('attachments')
        .filter(invoice =>
          ['invoice', 'invoice_xml', 'invoice_txt', 'invoice_image'].includes(
            invoice.get('fileType')
          )
        )
        .map(invoice => ({
          file: invoice?.get('file'),
          id: invoice?.get('id'),
          fileType: invoice?.get('fileType'),
        }))
        .toArray()

      const xmlFiles = invoiceList.filter(invoice => invoice.fileType === 'invoice_xml')
      const txtFiles = invoiceList.filter(invoice => invoice.fileType === 'invoice_txt')

      const invoiceFiles = invoiceList.filter(invoice => invoice.fileType === 'invoice')
      const invoicePdfFiles = invoiceFiles.filter(invoice =>
        invoice?.file?.toLowerCase()?.includes('.pdf')
      )
      const invoiceImageFiles = [
        ...invoiceFiles.filter(invoice => !invoice?.file?.toLowerCase()?.includes('.pdf')),
        ...invoiceList.filter(invoice => invoice.fileType === 'invoice_image'),
      ]

      setAttachments({
        pdf: invoicePdfFiles,
        image: invoiceImageFiles,
        xml: xmlFiles.length ? xmlFiles : txtFiles,
        txt: [],
      })

      setIsTxtFile(txtFiles.length > 0)
    }
  }, [serviceOrder?.get('attachments')])

  useEffect(() => {
    setLoading(true)
    dispatch(getIsXmlRequiredToFinishServiceOrder(serviceOrderId))
      .then(() => setLoading(false))
      .catch(() => setLoading(false))
  }, [])

  return (
    <Modal.Root open={open} onClose={onClose}>
      <Modal.TitleModal title="Nota Fiscal" />
      <Modal.Content className={styles.content}>
        <FormProvider {...methods}>
          <form className={styles.form} onSubmit={e => e.preventDefault()}>
            {serviceOrder && (
              <>
                <div className={styles.attachmentsContainer}>
                  <Typography
                    className={classNames(
                      styles.attachmentLabel,
                      styles.inputLabel,
                      !isPdfNotRequired && styles.required
                    )}
                  >
                    Nota fiscal formato PDF
                  </Typography>
                  <Attachments
                    label="Nota fiscal formato PDF"
                    content="files"
                    readOnly={!isEditing || (isSaasAgency && attachments?.image?.length > 0)}
                    files={attachments?.pdf}
                    multiple={false}
                    downloadable
                    accept={{ 'application/pdf': ['.pdf'] }}
                    onDrop={file => handleAddInvoice('pdf', file)}
                    onRemoveItem={() => handleRemoveInvoice('pdf')}
                    onItemClick={() => handleDownloadInvoice('pdf')}
                  />
                </div>
                {isSaasAgency && (
                  <div className={styles.attachmentsContainer}>
                    <Typography className={classNames(styles.attachmentLabel, styles.inputLabel)}>
                      Imagem da nota fiscal
                    </Typography>
                    <Attachments
                      label="Imagem da nota fiscal"
                      content="files"
                      readOnly={!isEditing || attachments?.pdf?.length > 0}
                      files={attachments?.image}
                      multiple={false}
                      downloadable
                      accept={{
                        'image/jpeg': ['.jpeg', '.png', '.jpg', '.bmp', '.webp'],
                      }}
                      onDrop={file => handleAddInvoice('image', file)}
                      onRemoveItem={() => handleRemoveInvoice('image')}
                      onItemClick={() => handleDownloadInvoice('image')}
                    />
                  </div>
                )}
                {isXmlRequiredToFinishServiceOrder?.isXmlRequired && (
                  <div className={styles.attachmentsContainer}>
                    <Typography
                      className={classNames(
                        styles.attachmentLabel,
                        styles.inputLabel,
                        isXmlRequiredToFinishServiceOrder?.isXmlRequired &&
                          !isXmlNotRequired &&
                          styles.required
                      )}
                    >
                      Nota fiscal formato XML ou TXT
                    </Typography>
                    <Attachments
                      label="Nota fiscal formato XML ou TXT"
                      content="files"
                      readOnly={!isEditing}
                      files={attachments?.xml.length ? attachments?.xml : attachments?.txt}
                      multiple={false}
                      downloadable
                      accept={{
                        'application/xml': [
                          '.xml',
                          '.addin',
                          '.asa',
                          '.txt',
                          '.asm',
                          '.json',
                          '.nfe',
                          '.nfse',
                          '.xmlnfe',
                        ],
                      }}
                      onDrop={file => {
                        const extension = file[0].name.split('.').pop().toLowerCase()
                        handleAddInvoice(extension, file)
                      }}
                      onRemoveItem={() => {
                        if (attachments?.xml.length) {
                          handleRemoveInvoice('xml')
                        } else {
                          handleRemoveInvoice('txt')
                        }
                      }}
                      onItemClick={() => {
                        if (attachments?.xml.length) {
                          handleDownloadInvoice('xml')
                        } else {
                          handleDownloadInvoice('txt')
                        }
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </form>
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <Modal.ButtonRed onClick={onClose}>Cancelar</Modal.ButtonRed>
        <Modal.ButtonFullBlue
          onClick={methods.handleSubmit(handleSendInvoice)}
          disabled={isSubmitButtonDisabled}
        >
          Confirmar
        </Modal.ButtonFullBlue>
      </Modal.Actions>
    </Modal.Root>
  )
}
